import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const GrowthPartnerPage = () => {
  const navigate = useNavigate();
  const [growthPartners, setGrowthPartners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    partner: null,
    tagSelect: '',
    clusters: [],
    chapters: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [dropdowns, setDropdowns] = useState({ tags: [], chapters: [], clusters: [] });

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  // Fetch dropdowns for tags, chapters, clusters
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [tagRes, chapterRes, clusterRes] = await Promise.all([
          axios.get('/admin/api/tags'), // Assuming there's a tags endpoint
          axios.get('/admin/api/chapters'),
          axios.get('/admin/api/clusters'),
        ]);
        
        setDropdowns({
          tags: (tagRes.data?.tags || tagRes.data || [])?.map(t => ({ value: t._id, label: t.name })) || [],
          chapters: chapterRes.data?.chapters?.map(c => ({ value: c._id, label: c.name, cluster: c.cluster })) || [],
          clusters: clusterRes.data?.clusters?.map(c => ({ value: c._id, label: c.name })) || [],
        });
      } catch (err) {
        console.error('Failed to fetch dropdowns:', err);
      }
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchGrowthPartners(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchGrowthPartners = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/growthPartner?page=${page}&limit=${limit}&search=${search}`);
      const { growthPartners = [], totalCount = 0 } = res.data;

      setGrowthPartners(growthPartners);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Growth Partners:', err);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      partner: { required: !editingId },
      tagSelect: { required: true },
      clusters: { required: true },
      chapters: { required: true },
    });

    // Additional validation for file size and type
    if (formValues.partner instanceof File) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (formValues.partner.size > maxSize) {
        validationErrors.partner = 'Image size should be less than 5MB';
      }
      
      if (!allowedTypes.includes(formValues.partner.type)) {
        validationErrors.partner = 'Only JPEG, PNG, GIF, and WebP images are allowed';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const formData = new FormData();
      
      if (formValues.partner instanceof File) {
        formData.append('partner', formValues.partner);
      }
      formData.append('tagSelect', formValues.tagSelect);
      formData.append('clusters', JSON.stringify(formValues.clusters));
      formData.append('chapters', JSON.stringify(formValues.chapters));

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editingId) {
        await axios.put(`/admin/api/growthPartner/${editingId}`, formData, config);
        toastSuccess('Growth Partner updated successfully!');
      } else {
        await axios.post('/admin/api/growthPartner', formData, config);
        toastSuccess('Growth Partner added successfully!');
      }
      setCurrentPage(1);
      fetchGrowthPartners(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Growth Partner:', err);
      toastError('Failed to save Growth Partner');
    }
  };

  const handleEdit = (growthPartner) => {
    setFormValues({
      partner: null, // Don't preload the image file for editing
      tagSelect: growthPartner.tagSelect || '',
      clusters: growthPartner.clusters || [],
      chapters: growthPartner.chapters || [],
    });
    setImagePreview(growthPartner.partner || '');
    setEditingId(growthPartner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this Growth Partner?')) {
      try {
        await axios.delete(`/admin/api/growthPartner/${id}`);
        toastSuccess('Growth Partner deleted successfully!');
        setCurrentPage(1);
        fetchGrowthPartners(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Growth Partner:', err);
        toastError('Failed to delete Growth Partner');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      partner: null,
      tagSelect: '',
      clusters: [],
      chapters: [],
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleImageChange = (name, file) => {
    setFormValues({ ...formValues, [name]: file });
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Growth Partners</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Growth Partners..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Growth Partner List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add Growth Partner
          </button>
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0" style={{ fontSize: '14px', lineHeight: '2' }}>
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Partner</th>
                <th className="py-1 px-2">Tag</th>
                <th className="py-1 px-2">Clusters</th>
                <th className="py-1 px-2">Chapters</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {growthPartners.length > 0 ? (
                growthPartners.map((partner, idx) => {
                  const tagLabel = dropdowns.tags.find(t => t.value === partner.tagSelect)?.label || '';
                  const clusterLabels = partner.clusters?.map(clusterId => 
                    dropdowns.clusters.find(c => c.value === clusterId)?.label
                  ).filter(Boolean).join(', ') || '';
                  const chapterLabels = partner.chapters?.map(chapterId => 
                    dropdowns.chapters.find(c => c.value === chapterId)?.label
                  ).filter(Boolean).join(', ') || '';
                  
                  return (
                    <tr key={partner._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">
                        {partner.partner ? (
                          <img 
                            src={partner.partner} 
                            alt="Partner" 
                            style={{ 
                              width: '40px', 
                              height: '40px', 
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div 
                            style={{ 
                              width: '40px', 
                              height: '40px', 
                              borderRadius: '50%',
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            👤
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-2">{tagLabel}</td>
                      <td className="py-1 px-2">{clusterLabels}</td>
                      <td className="py-1 px-2">{chapterLabels}</td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2"
                          style={{
                            fontSize: '12px',
                            lineHeight: '1.2',
                            boxShadow: 'none',
                            color: '#C44C23',
                            border: 'none'
                          }}
                          title="Edit"
                          onClick={() => handleEdit(partner)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2"
                          style={{
                            fontSize: '12px',
                            lineHeight: '1.2',
                            boxShadow: 'none',
                            color: 'red',
                            border: 'none'
                          }}
                          onClick={() => handleDelete(partner._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-2">No Growth Partners found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
               <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #C44C23'
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Growth Partner' : 'Create Cluster Growth Partner'}
        fields={[
          { 
            name: 'partner', 
            label: 'Partner', 
            type: 'file',
            placeholder: 'Select Partner Image',
            accept: 'image/*'
          },
          { 
            name: 'tagSelect', 
            label: 'Tag Select', 
            type: 'select', 
            options: dropdowns.tags, 
            placeholder: 'Select Tag' 
          },
          { 
            name: 'clusters', 
            label: 'Cluster', 
            type: 'select', 
            options: dropdowns.clusters, 
            placeholder: 'Select Cluster',
            isMulti: true
          },
          { 
            name: 'chapters', 
            label: 'Chapters', 
            type: 'select', 
            options: dropdowns.chapters, 
            placeholder: 'Select Chapter',
            isMulti: true
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={(updatedValues) => {
          if (updatedValues.partner instanceof File) {
            handleImageChange('partner', updatedValues.partner);
          } else if (updatedValues.partner === null) {
            setFormValues({ ...formValues, partner: null });
            setImagePreview('');
          } else {
            setFormValues(updatedValues);
          }
        }}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Submit'}
        requiredFields={editingId ? (imagePreview ? ['tagSelect', 'clusters', 'chapters'] : ['partner', 'tagSelect', 'clusters', 'chapters']) : ['partner', 'tagSelect', 'clusters', 'chapters']}
        imagePreview={imagePreview}
      />
    </div>
  );
};

export default GrowthPartnerPage;
